import { useEffect, useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';

import { Invitation } from '@cast/types';

import { useAbility } from '@cast/shared-ability-data-access';
import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const ORGANIZATION_INVITATIONS_QUERY_KEY = 'organization-invitations';
const getInvitationsQueryKey = (params: {
  organizationId: string | undefined;
}) => [ORGANIZATION_INVITATIONS_QUERY_KEY, params.organizationId] as const;

const getInvitationsQueryOptions = ({
  organizationId,
  enabled = true,
}: {
  organizationId?: string;
  enabled?: boolean;
} = {}) =>
  infiniteQueryOptions({
    queryKey: getInvitationsQueryKey({ organizationId }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.user.listInvitations({
        pageLimit: '100',
        pageCursor: pageParam,
      });
      return data || {};
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

type UseInvitationsQueryArgs = {
  enabled?: boolean;
};

export const useInvitationsQuery = (args?: UseInvitationsQueryArgs) => {
  const { enabled = true } = args || {};
  const { currentOrganization: organization } = useOrganizations();
  const canView = useAbility().can('view', 'OrganizationManagement');
  const query = useInfiniteQuery(
    getInvitationsQueryOptions({
      organizationId: organization?.id,
      enabled: enabled && !!organization?.id && canView,
    })
  );

  const invitations = useMemo(() => {
    return query.data
      ? query.data.pages.flatMap((page) => page.invitations as Invitation[])
      : [];
  }, [query.data]);

  useEffect(() => {
    if (query.hasNextPage) {
      query.fetchNextPage();
    }
  }, [query, query.hasNextPage, query.fetchNextPage]);

  return { invitations, ...query };
};
